import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'cva'
import * as React from 'react'

import { cn } from 'utils/StyleUtils'

const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-base font-medium transition-colors focus-visible:outline-hidden focus-visible:ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0`,
  {
    variants: {
      variant: {
        primary:
          'bg-spark-10 text-accent-contrast focus-visible:bg-spark-9 disabled:bg-spark-a3 disabled:text-spark-a7 hover:bg-spark-9',
        secondary:
          'bg-spark-a3 text-spark-a12 focus-visible:bg-spark-a4 disabled:bg-slate-a3 disabled:text-spark-a7 hover:bg-spark-a4',
        tertiary:
          'bg-transparent text-spark-a11 focus-visible:bg-slate-a3 disabled:bg-transparent disabled:text-spark-a7 hover:bg-slate-a3',
        bare: 'rounded-none! text-gray outline-hidden!',
        icon: 'rounded-md text-spark-a11 focus-visible:bg-slate-a3 hover:bg-slate-a3'
      },
      shape: {
        default:
          'rounded-[max(var(--spark-rounded-md),var(--spark-rounded-full))]'
      },
      size: {
        small: 'min-w-9 px-3 max-sm:h-10 md:h-9',
        default: 'h-11 min-w-10 px-3 max-sm:h-12',
        medium: 'h-12 min-w-12 px-4'
      }
    },
    defaultVariants: {
      shape: 'default',
      variant: 'primary',
      size: 'default'
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp: React.ElementType = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
) as React.FC<ButtonProps & { ref?: React.Ref<HTMLButtonElement> }>

Button.displayName = 'Button'

export { Button, buttonVariants }
